import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Chart as ChartJS,
  BarController,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  BarController,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const AMOUNT_COLOR = "#5CC7C1";
const QTY_COLOR = "#5C74C7";
const TC_FONT = "'Segoe UI', system-ui, -apple-system, sans-serif";
const LEGEND_FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";
const DONUT_COLORS = [
  "#5CC7C1",
  "#B2B3F5",
  "#52ABE1",
  "#5C74C7",
  "#F06547",
  "#3CC3DF",
  "#434549",
  "#FB83B3",
  "#6FD195",
  "#F4CF3B",
];

function fmtAmount(v) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${Math.round(v / 1_000)}k`;
  return String(v);
}

function TopNDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const items = [10, 20, 30].map((n) => ({ key: String(n), label: `Top ${n}` }));
  const selected = items.find((i) => i.key === value) || items[0];

  return (
    <div style={styles.dropdownWrap} ref={ref}>
      <div style={{ ...styles.control, width: 90,height:28 }} onClick={() => setOpen((v) => !v)}>
        {selected.label}
        <span style={styles.chevron}>▾</span>
      </div>
      {open && (
        <div style={styles.dropdownMenu}>
          {items.map((item) => (
            <div
              key={item.key}
              style={{
                ...styles.dropdownOption,
                background: value === item.key ? "#EEF4F3" : "#FFFFFF",
                fontWeight: value === item.key ? 600 : 400,
              }}
              onClick={() => {
                onChange(item.key);
                setOpen(false);
              }}
            >
              {item.label}
              {value === item.key ? <span style={styles.optionCheck}>✓</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ByTypeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const items = [
    { key: "Amount", label: "By Amount" },
    { key: "Qty", label: "By Qty" },
  ];
  const selected = items.find((i) => i.key === value) || items[0];

  return (
    <div style={styles.dropdownWrap} ref={ref}>
      <div style={{ ...styles.control, width: 110,height:28 }} onClick={() => setOpen((v) => !v)}>
        {selected.label}
        <span style={styles.chevron}>▾</span>
      </div>
      {open && (
        <div style={{ ...styles.dropdownMenu, minWidth: 130,height:28 }}>
          {items.map((item) => (
            <div
              key={item.key}
              style={{
                ...styles.dropdownOption,
                background: value === item.key ? "#EEF4F3" : "#FFFFFF",
                fontWeight: value === item.key ? 600 : 400,
              }}
              onClick={() => {
                onChange(item.key);
                setOpen(false);
              }}
            >
              {item.label}
              {value === item.key ? <span style={styles.optionCheck}>✓</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ThreeDotsMenu({ chartRef }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handlePrint = () => {
    window.print();
    setOpen(false);
  };

  const handleDownload = () => {
    const chart = chartRef?.current;
    if (!chart) return;
    const url = chart.toBase64Image();
    const link = document.createElement("a");
    link.href = url;
    link.download = "item-chart.png";
    link.click();
    setOpen(false);
  };

  return (
    <div style={styles.menuWrapper} ref={ref}>
      <button
        style={styles.dots}
        onClick={() => setOpen(!open)}
        type="button"
        title="More options"
      >
        ⋮
      </button>
      {open && (
        <div style={styles.menu}>
          <div style={styles.menuIconRow}>
            <button style={styles.menuIconBtn} onClick={handlePrint} title="Print" type="button">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
            </button>
            <button style={styles.menuIconBtn} onClick={handleDownload} title="Download" type="button">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
                <path d="M19 16 v3 M17.5 19 h3" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function makeBarOptions(maxVal, byType = "Amount") {
  const raw = maxVal > 0 ? maxVal / 5 : 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = Math.ceil(raw / mag) * mag;
  const max = Math.ceil(maxVal / step) * step || 5;
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "rectRounded",
          boxWidth: 8,
          boxHeight: 8,
          color: "#6b7280",
          padding: 12,
          font: { size: 10 },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: { maxRotation: 90, minRotation: 90, font: { size: 10 } },
      },
      y: {
        beginAtZero: true,
        max,
        ticks: {
          stepSize: step,
          callback: (v) => (byType === "Amount" ? fmtAmount(v) : v),
          color: "#7b7b7b",
          font: { size: 10 },
        },
        grid: { color: "rgba(0,0,0,0.08)", drawBorder: false },
      },
    },
  };
}

function sliceTopN(rows, n) {
  const num = parseInt(n, 10) || 10;
  return [...rows]
    .sort((a, b) => (Number(b.sales) || 0) - (Number(a.sales) || 0))
    .slice(0, num);
}

function DonutCard({ rows, onDragStart, onDragEnd }) {
  const [topN, setTopN] = useState("10");
  const [byType, setByType] = useState("Amount");
  const chartRef = useRef();
  const sliced = useMemo(() => sliceTopN(rows, topN), [rows, topN]);
  const data = {
    labels: sliced.map((r) => r.item_name),
    datasets: [
      {
        data: sliced.map((r) =>
          byType === "Amount" ? Number(r.sales) || 0 : Number(r.qty) || 0,
        ),
        backgroundColor: sliced.map((_, i) => DONUT_COLORS[i % DONUT_COLORS.length]),
        borderWidth: 0,
        cutout: "70%",
      },
    ],
  };
  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            style={styles.dragHandle}
            draggable
            title="Drag to reorder chart"
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            type="button"
          >
            <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
              <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
              <circle cx="3" cy="6" r="1" fill="#6B7280" />
              <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
              <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
              <circle cx="9" cy="6" r="1" fill="#6B7280" />
              <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
            </svg>
          </button>
          <p style={styles.title}>Individual Comparing target and sales</p>
        </div>
        <div style={styles.headerRight}>
          <TopNDropdown value={topN} onChange={setTopN} />
          <ByTypeDropdown value={byType} onChange={setByType} />
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <Doughnut
          ref={chartRef}
          data={data}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: "bottom",
                labels: {
                  usePointStyle: true,
                  pointStyle: "circle",
                  boxWidth: 8,
                  boxHeight: 8,
                  color: "rgba(0, 0, 0, 0.7)",
                  padding: 8,
                  font: { family: LEGEND_FONT, size: 12, weight: "400" },
                },
              },
              tooltip: {
                backgroundColor: "#FFFFFF",
                borderColor: "#E3E3E3",
                borderWidth: 1,
                titleColor: "#2A2A2A",
                bodyColor: "#2A2A2A",
                padding: 8,
                callbacks: {
                  label(ctx) {
                    const value = Number(ctx.raw || 0).toLocaleString();
                    const total = ctx.dataset.data.reduce((s, v) => s + Number(v || 0), 0);
                    const pct = total > 0 ? ((Number(ctx.raw || 0) / total) * 100).toFixed(0) : "0";
                    return ` ${ctx.label} ${value} (${pct}%)`;
                  },
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
}

function CompareBarCard({ rows, onDragStart, onDragEnd }) {
  const [topN, setTopN] = useState("10");
  const [byType, setByType] = useState("Amount");
  const chartRef = useRef();
  const sliced = useMemo(() => sliceTopN(rows, topN), [rows, topN]);

  const targetData = sliced.map((r) =>
    byType === "Amount" ? Number(r.target ?? r.sellingPrice ?? r.sales) || 0 : Number(r.qty) || 0,
  );
  const salesData = sliced.map((r) =>
    byType === "Amount" ? Number(r.sales) || 0 : Number(r.qty) || 0,
  );
  const maxVal = Math.max(...targetData, ...salesData, 0);

  const data = {
    labels: sliced.map((r) => r.item_name || "Item"),
    datasets: [
      {
        label: "Target",
        data: targetData,
        backgroundColor: AMOUNT_COLOR,
        barThickness: 8,
      },
      {
        label: "Sales",
        data: salesData,
        backgroundColor: QTY_COLOR,
        barThickness: 8,
      },
    ],
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            style={styles.dragHandle}
            draggable
            title="Drag to reorder chart"
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            type="button"
          >
            <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
              <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
              <circle cx="3" cy="6" r="1" fill="#6B7280" />
              <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
              <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
              <circle cx="9" cy="6" r="1" fill="#6B7280" />
              <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
            </svg>
          </button>
          <p style={styles.title}>Individual Comparing target and sales</p>
        </div>
        <div style={styles.headerRight}>
          <TopNDropdown value={topN} onChange={setTopN} />
          <ByTypeDropdown value={byType} onChange={setByType} />
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <Bar
          ref={chartRef}
          data={data}
          options={makeBarOptions(maxVal, byType)}
        />
      </div>
    </div>
  );
}

export default function ItemCharts({ rows = [] }) {
  const safeRows = useMemo(() => rows.filter((r) => r?.item_name), [rows]);
  const [chartOrder, setChartOrder] = useState(["donut", "bar"]);
  const [draggingChart, setDraggingChart] = useState(null);

  const renderChart = {
    donut: (id) => (
      <DonutCard
        rows={safeRows}
        onDragStart={() => setDraggingChart(id)}
        onDragEnd={() => setDraggingChart(null)}
      />
    ),
    bar: (id) => (
      <CompareBarCard
        rows={safeRows}
        onDragStart={() => setDraggingChart(id)}
        onDragEnd={() => setDraggingChart(null)}
      />
    ),
  };

  function handleDrop(targetId) {
    if (!draggingChart || draggingChart === targetId) return;
    const next = [...chartOrder];
    const fromIdx = next.indexOf(draggingChart);
    const toIdx = next.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setChartOrder(next);
    setDraggingChart(null);
  }

  return (
    <div style={styles.root}>
      <div style={styles.grid}>
        {chartOrder.map((cardId) => (
          <div
            key={cardId}
            style={styles.colItem}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(cardId)}
          >
            {renderChart[cardId](cardId)}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  root: { padding: "0 20px 16px", background: "#fff" },
  grid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 },
  colItem: { minWidth: 0, position: "relative" },
  card: {
    boxSizing: "border-box",
    background: "#FFFFFF",
    border: "1px solid #E3E3E3",
    borderRadius: 8,
    padding: "24px 16px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 0,
    paddingBottom: 24,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    flex: 1,
  },
  dragHandle: {
    cursor: "grab",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    borderRadius: 4,
    lineHeight: 0,
    background: "transparent",
    border: "none",
    padding: 0,
  },
  title: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: "#606060",
    fontFamily: TC_FONT,
    lineHeight: "24px",
    minWidth: 240,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
    marginLeft: "auto",
  },
  control: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid #ddd",
    borderRadius: 4,
    background: "#fff",
    color: "#606060",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: TC_FONT,
    padding: "2px 8px",
    boxSizing: "border-box",
    cursor: "pointer",
    userSelect: "none",
    lineHeight: "normal",
  },
  dropdownWrap: { position: "relative" },
  dropdownMenu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    background: "#FFFFFF",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    zIndex: 20,
    overflow: "hidden",
  },
  dropdownOption: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "9px 14px",
    fontFamily: TC_FONT,
    fontSize: 14,
    lineHeight: "normal",
    color: "#606060",
    cursor: "pointer",
  },
  optionCheck: {
    marginLeft: "auto",
    color: "#5CC7C1",
    fontSize: 14,
    fontWeight: 700,
  },
  chevron: {
    fontSize: 14,
    color: "#606060",
    marginLeft: 4,
    lineHeight: 1,
  },
  menuWrapper: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dots: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#606060",
    fontSize: 22,
    lineHeight: 1,
    padding: 0,
    userSelect: "none",
    outline: "none",
  },
  menu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 12px)",
    background: "#fff",
    border: "1px solid #e9e9e9",
    borderRadius: 8,
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.12)",
    zIndex: 50,
    padding: "8px 12px",
    minWidth: 110,
    height:28
  },
  menuIconRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  menuIconBtn: {
    width: 34,
    height: 34,
    border: "none",
    background: "transparent",
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#666",
    padding: 0,
  },
  chartWrap: { height: 487 },
};
